<template>
  <Dialog
      :header="modalTitle"
      v-model:visible="visible"
      modal
      class="institution-diploma-modal"
      :draggable="false"
      :dismissable-mask="true"
      :show-header="true"
      @hide="onHide"
  >
    <template v-if="!isNewDiploma">
      <div class="mb-2">
        <label for="id-time-added" class="form-label">{{ t('TIME_ADDED') }}</label>
        <InputText
            type="text"
            id="id-time-added"
            :model-value="dateFormat(modalData.createdAt || '') + ' ' + timeFormat(modalData.createdAt || '')"
            disabled
            class="w-100"
        />
      </div>

      <div class="mb-2">
        <label for="id-submitter" class="form-label">{{ t('SUBMITTER') }}</label>
        <InputText
            id="id-submitter"
            v-model="modalData.createdByName"
            disabled
            class="w-100"
        />
      </div>

      <div class="mb-2">
        <label for="id-account-name" class="form-label">{{ t('NAME') }}</label>
        <InputText
            id="id-account-name"
            v-model="modalData.accountName"
            disabled
            class="w-100"
        />
      </div>

      <div class="mb-2">
        <label for="id-account-ssn" class="form-label">{{ t('SSN') }}</label>
        <InputText
            id="id-account-ssn"
            v-model="modalData.accountSsn"
            disabled
            class="w-100"
        />
      </div>
    </template>

    <div class="mb-2">
      <label for="id-sender-email" class="form-label">{{ t('SENDER_EMAIL') }}</label>
      <InputText
          id="id-sender-email"
          type="email"
          autocomplete="off"
          name="senderEmail"
          v-model.trim="modalData.senderEmail"
          :disabled="isReadOnly"
          class="w-100"
          :invalid="emailState === false"
      />
      <small v-if="emailState === false" class="p-error">{{ emailFeedback }}</small>
    </div>

    <div class="mb-2" v-if="!isNewDiploma && !isReadOnly">
      <label for="id-show-file-upload" class="form-label">{{ t('UPLOAD_NEW_FILE') }}</label>
      <div>
        <Checkbox input-id="id-show-file-upload" v-model="showFileUpload" :binary="true"/>
      </div>
    </div>

    <div class="mb-2" v-if="showFileUpload && !isReadOnly">
      <label for="id-select-file" class="form-label">{{ t('SELECT_FILE') }}</label>
      <SingleFileUpload 
        id="id-select-file"
        :invalid="validationPending && !fileData" 
        @select="onFileSelect" 
        @remove="onFileClear"/>
    </div>

    <div class="mb-2">
      <label for="id-diploma-level" class="form-label">{{ t('LANGUAGE_DIPLOMA_LEVEL') }}</label>
      <Select
          label-id="id-diploma-level"
          v-model="modalData.diplomaLevel"
          :options="diplomaLevelSelectOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('SELECT')"
          :disabled="isReadOnly"
          class="w-100"
          :invalid="validationPending && !modalData.diplomaLevel"
      />
    </div>

    <div class="mb-2">
      <label for="id-document-type" class="form-label">{{ t('DOCUMENT_TYPE') }}</label>
      <Select
          label-id="id-document-type"
          v-model="modalData.documentType"
          :options="documentTypeSelectOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('SELECT')"
          :disabled="isReadOnly"
          class="w-100"
          :invalid="validationPending && !modalData.documentType"
      />
    </div>

    <div class="mb-2">
      <label for="id-document-number" class="form-label">{{ t('DOCUMENT_NUMBER') }}</label>
      <InputText
          id="id-document-number"
          v-model="modalData.documentNumber"
          :disabled="isReadOnly"
          class="w-100"
          :invalid="validationPending && !modalData.documentNumber"
      />
    </div>

    <div class="mb-2">
      <label for="id-institution" class="form-label">{{ t('INSTITUTION') }}</label>
      <search-input
          :on-input="searchInstitution"
          :model-value="institutionData"
          @on-change="onInstitutionChange"
          :disabled="isReadOnly"
          :state="validationPending ? !!institutionData : undefined"
          :required-symbols="1"
      />
    </div>

    <div class="mb-2">
      <label for="id-number-of-lessons" class="form-label">{{ t('NUMBER_OF_CONTACT_HOURS') }}</label>
      <InputNumber
          id="id-number-of-lessons"
          v-model="modalData.numberOfLessons"
          :showButtons="true"
          :disabled="isReadOnly"
          :min="1"
          inputClass="w-100"
          class="d-block"
          :invalid="validationPending && !validNumberOfLessons"
      />
    </div>

    <div class="mb-2">
      <label for="id-additional-info" class="form-label">{{ t('ADDITIONAL_INFO') }}</label>
      <Textarea
          id="id-additional-info"
          v-model="modalData.extraData"
          rows="3"
          class="w-100"
          :disabled="isReadOnly"
      />
    </div>

    <template v-if="!isReadOnly" #footer>
      <Button :label="t('CANCEL')" severity="secondary" @click="visible = false"/>
      <Button :label="t('SAVE')" :disabled="fileUploadPending" @click="addOrUpdateInstitutionDiploma"/>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {DiplomaDocumentType, DiplomaLevel, DiplomaStatus, type InstitutionDiplomaDto} from "@/model/institution-diploma";
import {useI18n} from "vue-i18n";
import {useToast} from "@/util/toastUtils";
import {useDateFormat} from "@/mixins/dateFormatMixin2";
import useTimeFormat from "@/mixins/timeFormatMixin2";
import SearchInput, {type Option} from "@/components/SearchInput.vue";
import SingleFileUpload from "@/components/SingleFileUpload.vue";
import {validateInteger, isValidEmail} from "@/util/validationUtils";
import type {InstitutionDto} from "@/model/institution";

const axios = injectAxios();
const {t} = useI18n();
const toast = useToast();

const {dateFormat} = useDateFormat();
const {timeFormat} = useTimeFormat();

const fileUploadPending = ref(false);
const validationPending = ref(false);

const fileData = ref<File | null>(null);
const institutionData = ref<Option | null>();
const modalData = ref<Partial<InstitutionDiplomaDto>>({});
const visible = ref(false);
const isNewDiploma = ref(false);
const showFileUpload = ref(false);
const referenceId = ref("");

const emit = defineEmits<{
  confirm: []
}>()

const emailState = computed(() => {
  if (!validationPending.value) return undefined;
  return !modalData.value.senderEmail || isValidEmail(modalData.value.senderEmail);
});

const emailFeedback = computed(() => {
  return emailState.value === false ? t("FAULTY_EMAIL") : "";
});

const validNumberOfLessons = computed(() => {
  return validateInteger(modalData.value.numberOfLessons, 1);
});

const newDocumentFormValid = computed(() => {
  return !!fileData.value
    && !!modalData.value
    && !!modalData.value.diplomaLevel
    && !!modalData.value.documentType
    && !!modalData.value.documentNumber
    && !!institutionData.value
    && validNumberOfLessons.value
    && (!modalData.value.senderEmail || isValidEmail(modalData.value.senderEmail));
});

const updateDocumentFormValid = computed(() => {
  return (!!fileData.value || !showFileUpload.value)
    && !!modalData.value
    && !!modalData.value.diplomaLevel
    && !!modalData.value.documentType
    && !!modalData.value.documentNumber
    && !!institutionData.value
    && validNumberOfLessons.value
    && (!modalData.value.senderEmail || isValidEmail(modalData.value.senderEmail));
});

const onInstitutionChange = (option: Option | null) => {
  institutionData.value = option;
};

const onFileSelect = (e: { files: File[] }) => {
  fileData.value = e.files?.[0] ?? null;
};

const onFileClear = () => {
  fileData.value = null;
};

const isReadOnly = computed(() => {
  return !isNewDiploma.value && DiplomaStatus.PENDING !== modalData.value.status && DiplomaStatus.REJECTED !== modalData.value.status;
});

const modalTitle = computed(() => {
  if (isReadOnly.value) {
    return t("VIEW_DOCUMENT");
  }

  if (isNewDiploma.value) {
    return t('ADD_NEW_DOCUMENT');
  }

  return t('EDIT_DOCUMENT');
});

const documentTypeSelectOptions = computed(() => {
  return Object.keys(DiplomaDocumentType).map(docType => ({
    label: t(`DOCUMENT_TYPES.${docType}`),
    value: docType,
  }));
});

const diplomaLevelSelectOptions = computed(() => {
  return Object.keys(DiplomaLevel).map(level => ({
    label: level,
    value: level,
  }));
});

const onHide = () => {
  visible.value = false;
};

const addOrUpdateInstitutionDiploma = async (ev: Event) => {
  ev.preventDefault();

  if (isNewDiploma.value) {
    await addInstitutionDiploma();
  } else {
    await updateInstitutionDiploma();
  }
};

const addInstitutionDiploma = async () => {
  if (!newDocumentFormValid.value) {
    validationPending.value = true;
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  try {
    validationPending.value = false;
    fileUploadPending.value = true;
    await axios.post(`/account/${referenceId.value}/institutions/diplomas`, buildMultipartForm(), {headers: getMultipartHeaders()});
    await afterSave();
  } finally {
    fileUploadPending.value = false;
  }
};

const updateInstitutionDiploma = async () => {
  if (!updateDocumentFormValid.value) {
    validationPending.value = true;
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  if (!showFileUpload.value) {
    fileData.value = null;
  }

  try {
    validationPending.value = false;
    fileUploadPending.value = true;
    await axios.put(`institutions/diplomas/${referenceId.value}`, buildMultipartForm(), {headers: getMultipartHeaders()});
    await afterSave();
  } finally {
    fileUploadPending.value = false;
  }
};

const searchInstitution = async (searchValue: string) => {
  const params = {
    searchValue: searchValue,
    isArchived: false,
  };

  const resp = await axios.get("/institutions", {params: params});
  return resp.data.content.map((row: InstitutionDto) => {
    return {
      value: row.id,
      label: row.businessName,
    };
  });
};

const afterSave = async () => {
  fileData.value = null;
  institutionData.value = null;
  modalData.value = {};
  visible.value = false;
  emit("confirm");
}

const openForNewDiploma = (id: string) => {
  referenceId.value = id;
  isNewDiploma.value = true;
  showFileUpload.value = true;
  validationPending.value = false;
  fileData.value = null;
  institutionData.value = null;
  modalData.value = {};
  visible.value = true;
};

const openToUpdateDiploma = (id: string, data: InstitutionDiplomaDto) => {
  referenceId.value = id;
  isNewDiploma.value = false;
  showFileUpload.value = false;
  validationPending.value = false;
  fileData.value = null;
  institutionData.value = {label: data.institutionName, value: data.institutionId};
  modalData.value = {...data};
  visible.value = true;
};

const getMultipartHeaders = () => {
  return {
    "Content-Type": "multipart/form-data",
    "Accept": "*/*",
  };
};

const buildMultipartForm = () => {
  const requestData: Record<string, unknown> = {
    ...modalData.value,
  };

  if (institutionData.value) {
    requestData["institutionId"] = institutionData.value.value;
  }

  if (fileData.value) {
    requestData["fileName"] = fileData.value.name;
  }

  const formData = new FormData();
  formData.append("request", new Blob([JSON.stringify(requestData)], {type: "application/json"}));

  if (fileData.value) {
    formData.append("file", fileData.value);
  }

  return formData;
};

defineExpose({
  openForNewDiploma,
  openToUpdateDiploma,
});
</script>

<style scoped>
.institution-diploma-modal :deep(.p-dialog) {
  width: 36rem;
}
</style>
